import React, { useState, useEffect } from "react";
import HToggle from "src/hcomponents/HToggle";
import HPageContainer from "../../../hcomponents/HPageContainer";
import SettingsTabsNavigation from "./SettingsTabsNavigation";
import HHeader from "../../../hcomponents/HHeader";
import HActionButton from "../../../hcomponents/HActionButton";
import { PencilLine, Trash2, Lock, Plus } from "lucide-react";
import axios from "axios";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../shadcn/ui/dialog";
import { Button } from "../../../shadcn/ui/button";
import { Constants } from "../../../utils/constants";

// API service for permissions
const createApiInstance = () => {
  const api = axios.create({
    baseURL: Constants.USER_API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Add auth token interceptor
  api.interceptors.request.use(config => {
    const accessToken = localStorage.getItem("access_token");
    const token = accessToken || localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return api;
};

const permissionService = {
  async getPermissions(filters = {}) {
    try {
      const api = createApiInstance();
      const params = new URLSearchParams();

      if (filters.page !== undefined) {
        params.append("page", filters.page.toString());
      }
      if (filters.limit !== undefined) {
        params.append("limit", filters.limit.toString());
      }
      if (filters.orderBy) {
        params.append("orderBy", filters.orderBy);
      }

      const response = await api.get(`/api/v1/permissions?${params.toString()}`);
      return {
        data: response.data,
        status: response.status,
        message: response.data?.message,
      };
    } catch (error) {
      logger.error("Error getting permissions:", error);
      throw error;
    }
  },

  async createPermission(payload) {
    try {
      const api = createApiInstance();
      const response = await api.post("/api/v1/permissions", payload);
      return {
        data: response.data,
        status: response.status,
        message: response.data?.message,
      };
    } catch (error) {
      logger.error("Error creating permission:", error);
      throw error;
    }
  },

  async updatePermission(id, payload) {
    try {
      const api = createApiInstance();
      const response = await api.put(`/api/v1/permissions/${id}`, payload);
      return {
        data: response.data,
        status: response.status,
        message: response.data?.message,
      };
    } catch (error) {
      logger.error("Error updating permission:", error);
      throw error;
    }
  },

  async deletePermission(id) {
    try {
      const api = createApiInstance();
      const response = await api.delete(`/api/v1/permissions/${id}`);
      return {
        data: response.data,
        status: response.status,
        message: response.data?.message,
      };
    } catch (error) {
      logger.error("Error deleting permission:", error);
      throw error;
    }
  },
};

const roles = [
  {
    name: "Admin",
    description: "Full access to all features and settings",
    users: 1,
    system: true,
    permissions: {
      "Manage Spaces": true,
      "Manage Bookings": true,
      "Manage Payments": true,
      "Manage Users": true,
      "View Reports": true,
      "Manage Settings": true,
      "API Access": true,
    },
  },
  {
    name: "Manager",
    description: "Can manage spaces, bookings, and view reports",
    users: 1,
    system: false,
    permissions: {
      "Manage Spaces": false,
      "Manage Bookings": true,
      "Manage Payments": true,
      "Manage Users": false,
      "View Reports": false,
      "Manage Settings": true,
      "API Access": false,
    },
  },
  {
    name: "Staff",
    description: "Can manage bookings and view spaces",
    users: 1,
    system: false,
    permissions: {
      "Manage Spaces": false,
      "Manage Bookings": true,
      "Manage Payments": true,
      "Manage Users": false,
      "View Reports": false,
      "Manage Settings": false,
      "API Access": false,
    },
  },
  {
    name: "Analyst",
    description: "Can view reports only",
    users: 1,
    system: false,
    permissions: {
      "Manage Spaces": false,
      "Manage Bookings": false,
      "Manage Payments": false,
      "Manage Users": false,
      "View Reports": true,
      "Manage Settings": false,
      "API Access": false,
    },
  },
];

export default function RolesPage() {
  const notificationTabs = ["Team Members", "Roles & Permissions"];
  const [tab, setTab] = useState("Roles & Permissions");
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null);

  // Fetch permissions on component mount
  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await permissionService.getPermissions({
        page: 1,
        limit: 50,
        orderBy: "name:asc",
      });

      if (response.status === 200) {
        setPermissions(response.data.data || []);
        logger.log("Permissions loaded:", response.data.data);
      } else {
        setError("Failed to load permissions");
        showApiMessage(response, "load permissions");
      }
    } catch (error) {
      logger.error("Error fetching permissions:", error);
      setError("Error loading permissions");
      showApiMessage(error, "load permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePermission = id => {
    setPermissionToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePermission = async () => {
    if (!permissionToDelete) return;

    try {
      const response = await permissionService.deletePermission(permissionToDelete);

      if (response.status === 200 || response.status === 204) {
        showApiMessage(response, "delete permission");
        fetchPermissions(); // Refresh the list
      } else {
        showApiMessage(response, "delete permission");
      }

      setShowDeleteConfirm(false);
      setPermissionToDelete(null);
    } catch (error) {
      logger.error("Error deleting permission:", error);
      showApiMessage(error, "delete permission");
      setShowDeleteConfirm(false);
      setPermissionToDelete(null);
    }
  };

  // Transform API data to match the UI structure
  const transformPermissionToRole = permission => {
    const permissions = {};

    // Convert permissions array to object format for UI
    if (permission.permissions && permission.permissions.length > 0) {
      permission.permissions.forEach(perm => {
        permissions[perm.name] = true; // All permissions are enabled by default
      });
    }

    return {
      id: permission.id,
      name: permission.name,
      description: `Permission with ${permission.permissions?.length || 0} permissions`,
      users: 1, // This would need to come from a different API
      system: permission.admin,
      permissions: permissions,
      createdAt: permission.createdAt,
      modifiedAt: permission.modifiedAt,
    };
  };

  const displayRoles = permissions.length > 0 ? permissions.map(transformPermissionToRole) : roles; // Fallback to hardcoded roles if no API data

  return (
    <HPageContainer>
      <HHeader />
      <div className="overflow-auto">
        <SettingsTabsNavigation />
        <div className="overflow-auto p-6 mx-3 mb-3 bg-white rounded-xl border border-gray-300 scrollbar-hide">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-lg font-semibold text-gray-900 sm:text-xl md:text-2xl">Available Roles</h1>
            <HActionButton
              icon={<Plus className="w-4 h-4" />}
              label="Create Role"
              // onClick={() => history.push('/host/add-invoice')}
              className="w-auto text-sm bg-black sm:w-auto"
            />
          </div>

          <div className="space-y-4">
            {loading ? (
              <div className="py-8 text-center">
                <div className="mx-auto w-8 h-8 rounded-full border-b-2 border-gray-900 animate-spin"></div>
                <p className="mt-2 text-gray-600">Loading permissions...</p>
              </div>
            ) : error ? (
              <div className="py-8 text-center">
                <p className="text-red-600">Error: {error}</p>
                <button
                  onClick={fetchPermissions}
                  className="px-4 py-2 mt-2 text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : (
              displayRoles.map((role, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-200">
                  <div className="px-3 py-2 bg-gray-50 rounded-t-xl">
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">{role.name}</h2>
                        <p className="text-sm text-gray-500">{role.description}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 items-center text-sm text-gray-500">
                        <span>{role.users} user</span>
                        {role.system && (
                          <span className="flex items-center gap-1 text-white text-xs font-normal px-2 py-0.5 rounded-lg bg-[#4C4AEF]">
                            <Lock size={12} />
                            System
                          </span>
                        )}

                        <PencilLine className="w-4 h-4 text-gray-500 cursor-pointer hover:text-black" />
                        <Trash2
                          className="w-4 h-4 text-red-300 cursor-pointer hover:text-red-600"
                          onClick={() => handleDeletePermission(role.id)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6 p-3 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-4">
                      {Object.entries(role.permissions)
                        .slice(0, 3)
                        .map(([perm, hasPermission], idx) => (
                          <div key={idx} className="flex gap-2 justify-between items-center text-sm">
                            <span className="text-gray-700">{perm}</span>
                            <HToggle checked={hasPermission} className="sm:mx-4" />
                          </div>
                        ))}
                    </div>
                    <div className="space-y-4">
                      {Object.entries(role.permissions)
                        .slice(3, 5)
                        .map(([perm, hasPermission], idx) => (
                          <div key={idx} className="flex gap-2 justify-between items-center text-sm">
                            <span className="text-gray-700">{perm}</span>
                            <HToggle checked={hasPermission} className="sm:mx-4" />
                          </div>
                        ))}
                    </div>
                    <div className="space-y-4">
                      {Object.entries(role.permissions)
                        .slice(5)
                        .map(([perm, hasPermission], idx) => (
                          <div key={idx} className="flex gap-2 justify-between items-center text-sm">
                            <span className="text-gray-700">{perm}</span>
                            <HToggle checked={hasPermission} className="sm:mx-4" />
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Permission</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this permission? This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteConfirm(false);
                setPermissionToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmDeletePermission} className="bg-[#3A36DB] hover:bg-[#2d2bb8] text-white">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </HPageContainer>
  );
}
